import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { whileInView } from "~/lib/web/loop";

/** Grid spacing in px, and the dot radius at each end of the range. */
const PITCH = { desktop: 10, mobile: 14 };
const DOT_MIN = 0.32;
const DOT_MAX = 2.1;
/** Exponent on the value before it becomes a radius. */
const GAMMA = 1.4;

/** The smallest radius every dot draws at, so the grid is always readable. */
const AMBIENT = 0.16;
/** Width of a ring's band in px, and how far it widens as it travels. */
const RING_WIDTH = 54;
const RING_SPREAD = 0.75;
/** Seconds of clock a ring takes to cross the field, and between spawns. */
const TRAVEL = 11;
const INTERVAL = 2.8;
/** Clock seconds per real second. */
const SPEED = 0.45;

/**
 * How far the type block damps the field, and how far out it is released.
 *
 * The clearing is a circle on the heading's longer side rather than an ellipse
 * on its box: the heading is wide and short, so an ellipse damps hard
 * vertically and barely at the sides, which splits the rings into two lobes.
 */
const CLEARING_DAMP = 0.35;
const CLEARING_FEATHER = 1.5;
const CLEARING_PAD = 64;

/** Blue, four stops, each mixed this far toward the page's own ground. */
const RAMP = ["#0099ff", "#0289ff", "#0f75ff", "#132066"];
const TINT = 0.48;
/**
 * One fillStyle change and one fill() per bucket rather than per dot.
 *
 * The palette is fixed: a dot's color comes from its distance and the clock,
 * so only which bucket it lands in changes between frames.
 */
const BUCKET_COUNT = 26;
/** Set once per frame on the context, not per dot. */
const GLOW_BLUR = 3;

/** Beyond this many band widths from a ring, its contribution rounds to zero. */
const RING_REACH = 3;

/**
 * Widest bitmap to ask for, whatever the device pixel ratio.
 *
 * A canvas wider than this is drawn without GPU acceleration in some browsers,
 * which puts a full field of arcs on the CPU every frame. Only a window wide
 * enough to need more than this reaches the cap, and the dots are a fixed size
 * in CSS pixels either way, so it costs resolution rather than density.
 */
const MAX_BITMAP_WIDTH = 4096;

const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/**
 * The halftone field behind the /pulse hero.
 *
 * A fixed grid of dots covers the section and only each dot's radius changes
 * between frames. Rings of enlarged dots spawn at the heading and expand
 * outward, decelerating; a dot's color is sampled separately, from its
 * distance to that same point, drifting on the clock.
 *
 * Everything that depends only on a dot's position is computed once per resize
 * into the arrays below, so a frame is one exp per ring in reach, one lookup
 * for the radius, and the arc.
 */
export default class WebHalftoneController extends Controller<HTMLElement> {
  static override targets = ["field", "clearing"];
  declare readonly fieldTarget: HTMLElement;
  declare readonly clearingTarget: HTMLElement;

  private canvas: HTMLCanvasElement | null = null;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private release: (() => void) | null = null;
  private observer: ResizeObserver | null = null;
  private frame = 0;
  private last = 0;
  private clock = 0;
  private lastSpawn = 0;
  private rings: number[] = [];

  private width = 0;
  private height = 0;
  private pitch = PITCH.desktop;
  private columns = 0;
  private rows = 0;
  private reach = 0;

  /** Per dot, in row-major order, all of it fixed until the next resize. */
  private distance = new Float32Array(0);
  /** exp(-(d/60)^2), the term that pulses at the emitter itself. */
  private core = new Float32Array(0);
  /** The band gate nearest the navbar, times the heading's clearing. */
  private damp = new Float32Array(0);
  /** The band gate at the section's bottom edge, applied to the radius. */
  private fade = new Float32Array(0);
  /** The dot's own offset into the color ramp. */
  private hue = new Float32Array(0);

  /** value ** GAMMA, since value only ever reaches the radius through this. */
  private readonly curve = Float32Array.from(
    { length: 256 },
    (_, i) => ((i + 0.5) / 256) ** GAMMA,
  );
  private palette: string[] = [];

  override connect(): void {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context === null) return;
    canvas.setAttribute("aria-hidden", "true");
    this.fieldTarget.appendChild(canvas);
    this.canvas = canvas;
    this.canvasContext = context;
    this.palette = this.buildPalette(context);

    this.measure();
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.element);

    if (prefersReducedMotion()) {
      // Four rings at fixed points of their travel, drawn once. The clock is
      // held at 0 so the frame is the same one on every load.
      this.rings = [0.16, 0.4, 0.63, 0.86].map((p) => -p * TRAVEL);
      this.draw();
      return;
    }
    this.release = whileInView(
      this.element,
      () => this.start(),
      () => this.stop(),
    );
  }

  override disconnect(): void {
    this.release?.();
    this.release = null;
    this.stop();
    this.observer?.disconnect();
    this.observer = null;
    this.canvas?.remove();
    this.canvas = null;
    this.canvasContext = null;
  }

  private start(): void {
    // Seeded with four already in flight, one interval apart, so the field is
    // not empty for the first travel.
    if (!this.rings.length) {
      this.rings = [0, 1, 2, 3].map((index) => -index * INTERVAL);
    }
    this.last = performance.now();
    this.frame = requestAnimationFrame((now) => this.tick(now));
  }

  private stop(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private tick(now: number): void {
    this.clock += ((now - this.last) / 1000) * SPEED;
    this.last = now;

    if (this.clock - this.lastSpawn >= INTERVAL) {
      this.lastSpawn += INTERVAL;
      this.rings.push(this.lastSpawn);
    }
    while (this.rings.length && this.clock - this.rings[0]! > TRAVEL) {
      this.rings.shift();
    }

    this.draw();
    this.frame = requestAnimationFrame((next) => this.tick(next));
  }

  private buildPalette(context: CanvasRenderingContext2D): string[] {
    const ground = getComputedStyle(this.element).getPropertyValue(
      "--web-background",
    );
    // Canvas normalizes any color it accepts to #rrggbb, which is the one
    // parser already in the page for a value that could be any CSS color.
    context.fillStyle = ground.trim() || "#ffffff";
    const paper = channels(context.fillStyle);
    const ramp = RAMP.map(channels);
    return Array.from({ length: BUCKET_COUNT }, (_, index) => {
      const position = ((index + 0.5) / BUCKET_COUNT) * ramp.length;
      const from = ramp[Math.floor(position) % ramp.length]!;
      const to = ramp[(Math.floor(position) + 1) % ramp.length]!;
      const between = position - Math.floor(position);
      const rgb = from.map((value, channel) => {
        const mixed = value + (to[channel]! - value) * between;
        return Math.round(mixed + (paper[channel]! - mixed) * TINT);
      });
      return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    });
  }

  private measure(): void {
    const canvas = this.canvas;
    const context = this.canvasContext;
    if (canvas === null || context === null) return;

    // Both rects before anything is written to the canvas: setting its style
    // width invalidates layout, so a rect read after that forces a reflow.
    const box = this.element.getBoundingClientRect();
    const heading = this.clearingTarget.getBoundingClientRect();
    this.width = box.width;
    this.height = box.height;
    if (!this.width || !this.height) return;

    const ratio = Math.min(
      2,
      window.devicePixelRatio || 1,
      MAX_BITMAP_WIDTH / this.width,
    );
    canvas.width = Math.round(this.width * ratio);
    canvas.height = Math.round(this.height * ratio);
    canvas.style.width = `${this.width}px`;
    canvas.style.height = `${this.height}px`;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    this.pitch =
      window.innerWidth >= DESKTOP_MIN_WIDTH ? PITCH.desktop : PITCH.mobile;
    this.columns = Math.ceil(this.width / this.pitch) + 1;
    this.rows = Math.ceil(this.height / this.pitch) + 1;
    this.reach = Math.sqrt(this.width ** 2 + this.height ** 2) * 0.92;

    const radius = Math.max(heading.width, heading.height) / 2 + CLEARING_PAD;
    const centerX = heading.left - box.left + heading.width / 2;
    const centerY = heading.top - box.top + heading.height / 2;

    const count = this.columns * this.rows;
    this.distance = new Float32Array(count);
    this.core = new Float32Array(count);
    this.damp = new Float32Array(count);
    this.fade = new Float32Array(count);
    this.hue = new Float32Array(count);

    // The rings spawn where the clearing is centered, so one measurement of
    // the heading serves both.
    const fadeFrom = this.height * 0.65;
    for (let row = 0; row < this.rows; row++) {
      const y = row * this.pitch;
      // The band nearest the navbar reads at 68%, up to full a fifth down.
      // Both ends are fractions of the section, so a shorter hero keeps the
      // proportions: at 85vh a third would leave the top of the field faint
      // across most of what is on screen at once.
      const top =
        0.68 + 0.32 * smoothstep(0.04 * this.height, 0.22 * this.height, y);
      const bottom = 1 - smoothstep(fadeFrom, this.height, y);
      for (let column = 0; column < this.columns; column++) {
        const index = row * this.columns + column;
        const x = column * this.pitch;
        const dx = x - centerX;
        const dy = y - centerY;
        const d = Math.sqrt(dx * dx + dy * dy);
        const near = d / 60;
        const out = d / radius;

        this.distance[index] = d;
        this.core[index] = Math.exp(-(near * near));
        this.damp[index] =
          top *
          (out <= 1
            ? CLEARING_DAMP
            : out < CLEARING_FEATHER
              ? CLEARING_DAMP +
                (1 - CLEARING_DAMP) * smoothstep(1, CLEARING_FEATHER, out)
              : 1);
        this.fade[index] = bottom;
        this.hue[index] = (d / this.reach) * 1.2;
      }
    }
    this.draw();
  }

  private draw(): void {
    const context = this.canvasContext;
    if (context === null || !this.distance.length) return;
    context.clearRect(0, 0, this.width, this.height);

    // Every term of a ring depends on its age alone, so all of it is computed
    // here rather than inside the dot loop.
    const bands = this.rings.flatMap((spawn) => {
      const p = (this.clock - spawn) / TRAVEL;
      if (p < 0 || p > 1) return [];
      const eased = 1 - (1 - p) ** 2.2;
      const width = RING_WIDTH * (1 + eased * RING_SPREAD);
      return [
        {
          at: eased * this.reach,
          width,
          peak: (1 - p) ** 0.85,
          from: eased * this.reach - RING_REACH * width,
          to: eased * this.reach + RING_REACH * width,
        },
      ];
    });

    const pulse = 0.5 + 0.3 * Math.sin(this.clock * 0.42);
    const drift = this.clock * 0.018;
    const span = DOT_MAX - DOT_MIN;
    const buckets = Array.from({ length: BUCKET_COUNT }, () => new Path2D());

    for (let row = 0; row < this.rows; row++) {
      const y = row * this.pitch;
      for (let column = 0; column < this.columns; column++) {
        const index = row * this.columns + column;
        const d = this.distance[index]!;

        let value = AMBIENT;
        for (const band of bands) {
          if (d < band.from || d > band.to) continue;
          const z = (d - band.at) / band.width;
          const contribution = Math.exp(-(z * z)) * band.peak;
          if (contribution > value) value = contribution;
        }
        const core = this.core[index]! * pulse;
        if (core > value) value = core;
        value = (value < 1 ? value : 1) * this.damp[index]!;

        const radius =
          (DOT_MIN + span * this.curve[(value * 255) | 0]!) * this.fade[index]!;
        if (radius <= 0.05) continue;

        const offset = this.hue[index]! - drift;
        const bucket = ((offset - Math.floor(offset)) * BUCKET_COUNT) | 0;
        const path =
          buckets[bucket < BUCKET_COUNT ? bucket : BUCKET_COUNT - 1]!;
        path.moveTo(column * this.pitch + radius, y);
        path.arc(column * this.pitch, y, radius, 0, Math.PI * 2);
      }
    }

    // One glow color for the whole field, from the middle of the ramp: a
    // shadow per bucket would restore the state changes the batching removes,
    // and at 3px on a 10px pitch the per-dot hue is not visible in it.
    context.shadowBlur = GLOW_BLUR;
    context.shadowColor = this.palette[BUCKET_COUNT >> 1]!;
    for (let bucket = 0; bucket < BUCKET_COUNT; bucket++) {
      context.fillStyle = this.palette[bucket]!;
      context.fill(buckets[bucket]!);
    }
    context.shadowBlur = 0;
  }
}

const channels = (hex: string): [number, number, number] => {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};
